import { useEffect, useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { linkCustomersToGraph } from "@/lib/customerGraphLink";
import {
  EMPTY_NEW_CUSTOMER_DRAFT,
  normalizeNewCustomerDraft,
  type NewCustomerDraft,
  type NewCustomerValidationErrors,
  validateNewCustomerDraft,
} from "@/lib/customerCreate";

interface CreateCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  customerTerm: string;
  onCreated: (customerId: string) => void;
}

const CreateCustomerDialog = ({
  open,
  onOpenChange,
  businessId,
  customerTerm,
  onCreated,
}: CreateCustomerDialogProps) => {
  const [draft, setDraft] = useState<NewCustomerDraft>({ ...EMPTY_NEW_CUSTOMER_DRAFT });
  const [errors, setErrors] = useState<NewCustomerValidationErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) return;
    setDraft({ ...EMPTY_NEW_CUSTOMER_DRAFT });
    setErrors({});
    setSaving(false);
  }, [open]);

  const updateField = <Field extends keyof NewCustomerDraft>(field: Field, value: NewCustomerDraft[Field]) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationErrors = validateNewCustomerDraft(draft);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSaving(true);
    const input = normalizeNewCustomerDraft(draft);
    const { data, error } = await supabase
      .from("customers")
      .insert({ ...input, business_id: businessId })
      .select("customer_id")
      .single();
    setSaving(false);

    if (error) {
      if (error.code === "23505") {
        toast.error(`A ${customerTerm.toLowerCase()} with this email or phone already exists`);
      } else {
        toast.error(`Failed to create ${customerTerm.toLowerCase()}: ${error.message}`);
      }
      return;
    }

    toast.success(`${customerTerm} created`);
    onOpenChange(false);
    onCreated(data.customer_id);
    // Give the new record a contact profile (status, Analytics, AI). It
    // doesn't block the save; the Customers page retries any that miss.
    void linkCustomersToGraph(businessId, [data.customer_id]).then((linked) => {
      if (linked.length > 0) onCreated(data.customer_id);
    });
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !saving && onOpenChange(nextOpen)}>
      <DialogContent className="admin-modal-content">
        <DialogHeader className="admin-modal-header">
          <DialogTitle className="font-heading">Create {customerTerm.toLowerCase()}</DialogTitle>
          <DialogDescription>
            Add someone to your {customerTerm.toLowerCase()} list manually. They will not be contacted automatically.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" noValidate onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-customer-name">Name <span className="text-destructive">*</span></Label>
              <Input
                id="new-customer-name"
                autoFocus
                autoComplete="name"
                maxLength={200}
                placeholder="Avery Tan"
                value={draft.name}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "new-customer-name-error" : undefined}
                className={cn(errors.name && "border-destructive focus-visible:ring-destructive")}
                onChange={(event) => updateField("name", event.target.value)}
              />
              {errors.name && <p id="new-customer-name-error" className="text-xs text-destructive">{errors.name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-customer-email">Email</Label>
              <Input
                id="new-customer-email"
                type="email"
                autoComplete="email"
                maxLength={320}
                placeholder="avery@example.com"
                value={draft.email}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "new-customer-email-error" : undefined}
                className={cn(errors.email && "border-destructive focus-visible:ring-destructive")}
                onChange={(event) => updateField("email", event.target.value)}
              />
              {errors.email
                ? <p id="new-customer-email-error" className="text-xs text-destructive">{errors.email}</p>
                : <p className="text-xs text-muted-foreground">Add an email, a phone number, or both.</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-customer-phone">Phone number</Label>
              <div className={cn(
                "rounded-md border border-input bg-card px-3 py-2 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                errors.phone && "border-destructive focus-within:ring-destructive",
              )}>
                <PhoneInput
                  id="new-customer-phone"
                  international
                  defaultCountry="SG"
                  autoComplete="tel"
                  placeholder="+65 8123 4567"
                  value={draft.phone || undefined}
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "new-customer-phone-error" : undefined}
                  className="phone-input-onboarding"
                  onChange={(value) => updateField("phone", value ?? "")}
                />
              </div>
              {errors.phone && <p id="new-customer-phone-error" className="text-xs text-destructive">{errors.phone}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="new-customer-notes">Notes <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Textarea
                id="new-customer-notes"
                maxLength={2000}
                placeholder="Preferences or context about this customer"
                value={draft.notes}
                aria-invalid={!!errors.notes}
                aria-describedby={errors.notes ? "new-customer-notes-error" : "new-customer-notes-count"}
                className={cn("min-h-24 resize-y", errors.notes && "border-destructive focus-visible:ring-destructive")}
                onChange={(event) => updateField("notes", event.target.value)}
              />
              <div className="flex justify-between gap-3">
                {errors.notes ? <p id="new-customer-notes-error" className="text-xs text-destructive">{errors.notes}</p> : <span />}
                <p id="new-customer-notes-count" className="text-xs text-muted-foreground">{draft.notes.length}/2000</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="standard-action-button admin-modal-secondary-button neutral-action-button"
              disabled={saving}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" className="standard-action-button" disabled={saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {saving ? "Creating..." : `Create ${customerTerm.toLowerCase()}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCustomerDialog;
